import React from 'react';
import {
  Box, Card, CardContent, Typography, Button, Skeleton, Fade, Stack
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import { modernTheme, formatHoursMinutes, formatHoursMinutesFull, calcEarning, formatEarningShort, iconBoxSx } from './theme';

const TimelineDot = ({ color = modernTheme.colors.primary }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
    <Box sx={{
      width: 12, height: 12, borderRadius: '50%',
      bgcolor: color, border: '3px solid white',
      boxShadow: `0 0 0 2px ${color}30`,
      zIndex: 1,
    }} />
  </Box>
);

const RecordItem = ({ record, hourlyRate, isLast, isMobile }) => {
  const earning = calcEarning(record, hourlyRate);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start',
      position: 'relative',
      pb: isLast ? 0 : 2.5,
      '&::before': isLast ? {} : {
        content: '""',
        position: 'absolute',
        left: 5.5, top: 15, bottom: 0,
        width: 1.5,
        bgcolor: modernTheme.colors.gray[200],
      },
    }}>
      <TimelineDot color={modernTheme.colors.primary} />
      <Box sx={{
        flex: 1,
        p: { xs: 1.5, md: 2 },
        bgcolor: 'white',
        borderRadius: 2,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: modernTheme.colors.gray[50],
          borderColor: modernTheme.colors.primary,
          boxShadow: modernTheme.shadows.sm,
        },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.3 }}>
              {new Date(record.date).toLocaleDateString('vi-VN', {
                timeZone: 'Asia/Ho_Chi_Minh',
                weekday: isMobile ? 'short' : 'long',
                month: 'short', day: 'numeric'
              })}
            </Typography>
            <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontSize: '0.7rem' }}>
              {new Date(record.createdAt).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 1.5,
              bgcolor: `${modernTheme.colors.primary}12`,
              border: `1px solid ${modernTheme.colors.primary}25`,
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.primary, fontSize: '0.75rem' }}>
                {isMobile ? formatHoursMinutes(record) : formatHoursMinutesFull(record)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.success, fontSize: '0.75rem' }}>
              {formatEarningShort(earning)}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

const LoadingSkeleton = () => (
  <Stack spacing={2}>
    {[1, 2, 3].map((i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={12} height={12} />
        <Skeleton variant="rectangular" width="100%" height={52} sx={{ borderRadius: 2 }} />
      </Box>
    ))}
  </Stack>
);

const RecentActivity = ({ monthlySummary, userSummary, loading, onViewAll, isMobile }) => {
  const records = (monthlySummary?.records || [])
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);
  const hourlyRate = userSummary?.hourlyRate ?? 0;

  return (
    <Fade in timeout={1000}>
      <Card sx={{
        borderRadius: 3,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        boxShadow: modernTheme.shadows.md,
        background: modernTheme.gradients.card,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          p: { xs: 2, md: 2.5 },
          borderBottom: `1px solid ${modernTheme.colors.gray[200]}`,
          background: modernTheme.colors.gray[50],
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={iconBoxSx(modernTheme.gradients.accent, 40)}>
              <HistoryIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.2 }}>
                Lịch sử gần đây
              </Typography>
              <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500] }}>
                {monthlySummary?.records?.length || 0} bản ghi tháng này
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined" size="small"
            startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
            onClick={onViewAll}
            sx={{
              borderColor: modernTheme.colors.accent,
              color: modernTheme.colors.accent,
              borderRadius: 2, textTransform: 'none',
              fontWeight: 600, fontSize: '0.75rem',
              '&:hover': { borderColor: modernTheme.colors.accent, bgcolor: `${modernTheme.colors.accent}08` },
            }}
          >
            Xem tất cả
          </Button>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          {loading ? (
            <LoadingSkeleton />
          ) : records.length > 0 ? (
            <Box>
              {records.map((record, index) => (
                <RecordItem
                  key={record._id}
                  record={record}
                  hourlyRate={hourlyRate}
                  isLast={index === records.length - 1}
                  isMobile={isMobile}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, md: 5 } }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: 3,
                bgcolor: modernTheme.colors.gray[100],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 2,
              }}>
                <HistoryIcon sx={{ fontSize: 28, color: modernTheme.colors.gray[400] }} />
              </Box>
              <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[600], fontWeight: 700, mb: 0.5 }}>
                Chưa có lịch sử chấm công
              </Typography>
              <Typography variant="body2" sx={{ color: modernTheme.colors.gray[500], mb: 2 }}>
                Bạn chưa có lịch sử chấm công nào trong tháng này
              </Typography>
              <Button
                variant="outlined" startIcon={<EventIcon />} onClick={onViewAll}
                sx={{
                  color: modernTheme.colors.primary, borderColor: modernTheme.colors.primary,
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                }}
              >
                Xem lịch sử tổng thể
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

export default RecentActivity;
